import type {
    ClientRequest,
    ClientResponse,
    CustomObject,
    CustomObjectDraft,
    CustomObjectPagedQueryResponse,
} from '@commercetools/platform-sdk';

import type { ApiClientDetails, QueryArgs } from '../../types';
import { extendQueryArgs, getQueryPredicate, QueryKey } from '../utils';

export function customObjectApi({ apiRoot, ctpClient }: ApiClientDetails) {
    function customObjectClientRequest(args?: QueryArgs): ClientRequest {
        return apiRoot().customObjects().get(args).clientRequest();
    }

    async function createCustomObject(draft: CustomObjectDraft): Promise<ClientResponse<CustomObject>> {
        return apiRoot()
            .customObjects()
            .post({
                body: draft,
            })
            .execute();
    }

    async function saveCustomObject<T extends CustomObjectDraft, R extends CustomObject>(customObject: T): Promise<R> {
        const result = await apiRoot()
            .customObjects()
            .post({
                body: customObject,
            })
            .execute();

        return <R>result.body;
    }

    async function saveCustomObjects(customObjects: CustomObjectDraft[]): Promise<CustomObject[]> {
        return Promise.all(customObjects.map(customObject => saveCustomObject(customObject)));
    }

    function removeCustomObject({ container, key }: CustomObject): Promise<ClientResponse<CustomObject>> {
        return apiRoot().customObjects().withContainerAndKey({ container, key }).delete().execute();
    }

    function removeCustomObjects(customObjects: CustomObject[]): Promise<ClientResponse<CustomObject>>[] {
        return customObjects.map(customObject => removeCustomObject(customObject));
    }

    function getCustomObjects(args?: QueryArgs): Promise<ClientResponse<CustomObjectPagedQueryResponse>> {
        return apiRoot().customObjects().get(args).execute();
    }

    async function getCustomObject<R extends CustomObject>(args?: QueryArgs): Promise<R> {
        const [response] = (await getCustomObjects(args)).body.results;
        return <R>response;
    }

    async function getCustomObjectByContainerAndKey<R extends CustomObject>(
        container: string,
        key: string,
        expand?: string | string[]
    ): Promise<R | undefined> {
        return getCustomObject<R>({
            queryArgs: {
                where: [`container="${container}"`, `key="${key}"`],
                expand,
            },
        });
    }

    async function getCustomObjectById(id: string): Promise<CustomObject | undefined> {
        return (
            await apiRoot()
                .customObjects()
                .get({ queryArgs: { where: [`id = "${id}"`] } })
                .execute()
        ).body.results[0];
    }

    async function getCustomObjectsByIds(ids: string[]): Promise<CustomObject[]> {
        return (
            await apiRoot()
                .customObjects()
                .get({
                    queryArgs: { where: getQueryPredicate(ids, QueryKey.ID) },
                })
                .execute()
        ).body.results;
    }

    async function getCustomObjectByContainerAndId(container: string, id: string): Promise<CustomObject> {
        const result = await apiRoot()
            .customObjects()
            .withContainer({ container })
            .get({
                queryArgs: {
                    where: `id = "${id}"`,
                },
            })
            .execute();

        return result.body.results[0];
    }

    async function getCustomObjectsWhere<T extends CustomObject = CustomObject>(
        where: string | string[],
        expand?: string | string[]
    ): Promise<T[]> {
        const request = customObjectClientRequest({
            queryArgs: {
                where,
                expand,
            },
        });

        return ctpClient().process(request, payload => Promise.resolve(payload.body.results), {
            accumulate: true,
        });
    }

    async function fetchCustomObjects(container?: string, args?: QueryArgs, total?: number): Promise<CustomObject[]> {
        const request = customObjectClientRequest(
            extendQueryArgs(
                {
                    queryArgs: {
                        where: container ? [`container="${container}"`] : [],
                    },
                },
                args
            )
        );

        return ctpClient().process(request, payload => Promise.resolve(payload.body.results), {
            accumulate: true,
            ...(total && { total }),
        });
    }

    function getCustomObjectsByContainer(container: string, expand?: string | string[]): Promise<CustomObject[]> {
        return fetchCustomObjects(container, {
            queryArgs: {
                expand,
            },
        });
    }
    return {
        createCustomObject,
        saveCustomObject,
        saveCustomObjects,
        removeCustomObject,
        removeCustomObjects,
        fetchCustomObjects,
        getCustomObjectById,
        getCustomObjectsByIds,
        getCustomObjects,
        getCustomObject,
        getCustomObjectsByContainer,
        getCustomObjectByContainerAndKey,
        getCustomObjectByContainerAndId,
        getCustomObjectsWhere,
    };
}
